// Catalogo completo della bottega: ricerca e ordinamento in una barra dedicata,
// categorie su una riga a scorrimento sotto (mai sovrapposte), riepilogo dei filtri.
import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowUpDown, PackageSearch, Search, X } from 'lucide-react';
import { getChildCategoryName } from '../../utils/categoryPath';
import { createAvailabilityComparator } from '../../utils/productAvailability';
import { useShop } from '../ShopContext';
import ShopProductCard from '../ShopProductCard';
import { PageHeader } from '../ShopUi';

const categoryOf = (p) => {
  const path = p.customization_options?.categoryPath;
  return getChildCategoryName(path) || path?.sub || p.category || 'Altro';
};

const SORTS = [
  { id: 'newest', label: 'Più recenti' },
  { id: 'price-asc', label: 'Prezzo crescente' },
  { id: 'price-desc', label: 'Prezzo decrescente' },
];

export default function ShopProducts() {
  const { products, style, name } = useShop();
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('newest');
  const category = searchParams.get('categoria') || 'all';

  const setCategory = (c) => {
    const next = new URLSearchParams(searchParams);
    if (c === 'all') next.delete('categoria'); else next.set('categoria', c);
    setSearchParams(next, { replace: true });
  };

  const categories = useMemo(() => {
    const counts = {};
    products.forEach((p) => { const c = categoryOf(p); counts[c] = (counts[c] || 0) + 1; });
    return Object.entries(counts).map(([n, count]) => ({ name: n, count }));
  }, [products]);

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    const out = products.filter((p) =>
      (category === 'all' || categoryOf(p) === category) &&
      (!q || p.title.toLowerCase().includes(q) || (p.description || '').toLowerCase().includes(q)));
    const second = sort === 'price-asc' ? (a, b) => a.base_price - b.base_price
      : sort === 'price-desc' ? (a, b) => b.base_price - a.base_price
        : (a, b) => new Date(b.created_at) - new Date(a.created_at);
    return [...out].sort(createAvailabilityComparator(second));
  }, [products, query, category, sort]);

  const filtered = !!query.trim() || category !== 'all';
  const reset = () => { setQuery(''); setCategory('all'); };
  const shape = style.btn;
  const chip = (active) => `inline-flex items-center gap-1.5 h-9 px-4 ${shape} text-sm border whitespace-nowrap transition-colors ${
    active
      ? 'bg-shop-primary border-shop-primary text-white shadow-sm'
      : 'bg-white border-slate-200 text-slate-600 hover:border-shop-primary hover:text-shop-primary'}`;

  return (
    <div data-testid="shop-products">
      <PageHeader title="Le creazioni" subtitle={`${products.length} ${products.length === 1 ? 'creazione' : 'creazioni'} di ${name}, fatte a mano.`} />

      <div className="max-w-6xl mx-auto px-4">
        {/* Barra: ricerca + ordinamento */}
        <div className={`flex flex-col sm:flex-row gap-3 ${style.centered ? 'sm:max-w-2xl sm:mx-auto' : ''}`} data-testid="shop-products-toolbar">
          <label className="relative flex-1 min-w-0">
            <span className="sr-only">Cerca nella bottega</span>
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Cerca tra le creazioni di ${name}`}
              className={`w-full h-11 pl-11 pr-10 ${shape} border border-slate-200 bg-white text-sm shadow-sm focus:outline-none focus:border-shop-primary focus:ring-2 focus:ring-shop-primary/20`}
              data-testid="shop-products-search"
            />
            {query && (
              <button type="button" onClick={() => setQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full text-slate-400 hover:text-slate-700" aria-label="Cancella ricerca">
                <X className="w-4 h-4" />
              </button>
            )}
          </label>
          <label className="relative sm:w-52 shrink-0">
            <span className="sr-only">Ordina</span>
            <ArrowUpDown className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className={`w-full h-11 pl-11 pr-8 ${shape} border border-slate-200 bg-white text-sm shadow-sm appearance-none cursor-pointer focus:outline-none focus:border-shop-primary focus:ring-2 focus:ring-shop-primary/20`}
              data-testid="shop-products-sort"
            >
              {SORTS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-xs">▾</span>
          </label>
        </div>

        {/* Categorie: riga a scorrimento con sfumatura ai bordi */}
        {categories.length > 1 && (
          <div
            className="mt-4 -mx-4 px-4 overflow-x-auto scrollbar-hide [mask-image:linear-gradient(to_right,transparent,black_16px,black_calc(100%-16px),transparent)]"
            data-testid="shop-products-categories"
          >
            <div className={`flex gap-2 w-max py-1 ${style.centered ? 'mx-auto' : ''}`} role="tablist" aria-label="Categorie">
              <button type="button" role="tab" aria-selected={category === 'all'} className={chip(category === 'all')} onClick={() => setCategory('all')}>
                Tutte <span className="opacity-70 text-xs">{products.length}</span>
              </button>
              {categories.map((c) => (
                <button key={c.name} type="button" role="tab" aria-selected={category === c.name} className={chip(category === c.name)} onClick={() => setCategory(c.name)}>
                  {c.name} <span className="opacity-70 text-xs">{c.count}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Riepilogo */}
        <div className={`mt-5 mb-8 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500 ${style.centered ? 'justify-center' : ''}`}>
          <span><strong className="text-slate-800">{list.length}</strong> {list.length === 1 ? 'creazione' : 'creazioni'}</span>
          {query.trim() && <span>per «{query.trim()}»</span>}
          {category !== 'all' && <span>in {category}</span>}
          {filtered && (
            <button type="button" onClick={reset} className="inline-flex items-center gap-1 text-shop-primary hover:underline underline-offset-4">
              <X className="w-3.5 h-3.5" /> Azzera filtri
            </button>
          )}
        </div>

        {list.length ? (
          <div className={style.grid}>{list.map((p) => <ShopProductCard key={p.id} product={p} />)}</div>
        ) : (
          <div className={`py-16 px-6 text-center ${style.panel}`}>
            <PackageSearch className="w-10 h-10 mx-auto text-slate-300" />
            <p className="mt-3 font-medium text-slate-700">Nessuna creazione corrisponde alla ricerca</p>
            <p className="text-sm text-slate-500 mt-1">Prova con un'altra parola o guarda tutte le creazioni.</p>
            <button type="button" onClick={reset} className={`mt-5 inline-flex items-center h-10 px-5 ${shape} border border-shop-primary text-shop-primary text-sm font-medium hover:bg-shop-primary/5`}>
              Vedi tutte le creazioni
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
